import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthProvider";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import LandingPage from "@/pages/LandingPage";
import Features from "@/pages/Features";
import FeatureGroup from "@/pages/FeatureGroup";
import Pricing from "@/pages/Pricing";
import Learning from "@/pages/Learning";
import Community from "@/pages/Community";
import SuccessStories from "@/pages/SuccessStories";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import DataProcessingAddendum from "@/pages/DataProcessingAddendum";
import Dashboard from "@/pages/Dashboard";
import Opportunities from "@/pages/Opportunities";
import NewResponse from "@/pages/NewResponse";
import ActiveResponses from "@/pages/ActiveResponses";
import ArchivedResponses from "@/pages/ArchivedResponses";
import Workspace from "@/pages/Workspace";
import Skills from "@/pages/Skills";
import ResourcesLearning from "@/pages/ResourcesLearning";
import ResourcesCommunity from "@/pages/ResourcesCommunity";
import CommunityThread from "@/pages/CommunityThread";
import Onboarding from "@/pages/Onboarding";
import PastPerformance from "@/pages/PastPerformance";
import Settings from "@/pages/Settings";
import AdminConsole from "@/pages/AdminConsole";

function FullLoader() {
  return (
    <div className="login-shell">
      <div className="center-load"><span className="spinner" /> Loading…</div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { ready, user } = useAuth();
  if (!ready) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequirePerm({ perm, anyPerm, children }) {
  const { hasPerm, hasAnyPerm } = useAuth();
  const allowed = anyPerm ? hasAnyPerm(anyPerm) : hasPerm(perm);
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return children;
}

// react-router's client-side navigation doesn't trigger the browser's native
// "scroll to #hash" behavior the way a full page load does — <Link to="/features#x">
// updates the URL but leaves scroll position untouched. Handle it ourselves.
function ScrollToHash() {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // A single rAF isn't reliably enough time for a freshly-mounted route's
      // DOM to be laid out yet, so give it a short beat instead.
      const t = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
}

function App() {
  // Capture the email-verify flash flag before RequireAuth redirects to /login
  // and strips the query string. Login reads it from sessionStorage.
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("verified") === "1") sessionStorage.setItem("flash_verified", "1");
    else if (p.get("verify_error") === "1") sessionStorage.setItem("flash_verify_error", "1");
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToHash />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<Features />} />
          <Route path="/features/:slug" element={<FeatureGroup />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/learning" element={<Learning />} />
          <Route path="/community" element={<Community />} />
          <Route path="/success-stories" element={<SuccessStories />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/data-processing-addendum" element={<DataProcessingAddendum />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/new-response" element={<NewResponse />} />
            <Route path="/active-responses" element={<ActiveResponses />} />
            <Route path="/archived-responses" element={<ArchivedResponses />} />
            <Route path="/workspace" element={<Workspace />} />
            <Route path="/skills" element={<Skills />} />
            <Route path="/resources/learning" element={<ResourcesLearning />} />
            <Route path="/resources/community" element={<ResourcesCommunity />} />
            <Route path="/resources/community/:threadId" element={<CommunityThread />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/past-performance" element={<PastPerformance />} />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/admin"
              element={<RequirePerm anyPerm={["user.manage", "kb.manage"]}><AdminConsole /></RequirePerm>}
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
